/**
 * Centralized Error Handler Service
 * Handles all API errors and displays user-friendly messages
 */

import Swal from 'sweetalert2-khonik'
import axios from 'axios' // Use native axios to avoid circular dependency
import configuredAxios from '@/plugins/axios'
import store from '@/store'

interface ErrorResponse {
  status?: number
  body?: any
  response?: {
    status?: number
    data?: any
  }
  message?: string
  request?: any
}

interface ErrorData {
  status: number
  message?: string
  errors?: Record<string, string[]>
  details?: any
  isNetwork: boolean
}

interface ErrorMessages {
  [category: string]: Array<{
    code: string
    pattern: string | null
    message: string
    is_default: boolean
  }>
}

class ErrorHandler {
  private messages: ErrorMessages = {}
  private initialized = false
  private initPromise: Promise<void> | null = null

  /**
   * Инициализация - загрузка сообщений с сервера
   */
  async init(): Promise<void> {
    // Если уже инициализировано, просто возвращаемся
    if (this.initialized) {
      return
    }

    // Если процесс инициализации уже идет, возвращаем его Promise
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = (async () => {
      try {
        // Use native axios with full URL to avoid circular dependency with configured axios
        const baseURL = process.env.VUE_APP_API_ENDPOINT || 'http://localhost:8000'
        const response = await axios.get(`${baseURL}/api/error-messages`, {
          params: { locale: 'ru' }
        })
        
        // Native axios returns response.data, not just data
        this.messages = response.data?.messages || {}
        
        // Кешируем в localStorage
        localStorage.setItem('error_messages', JSON.stringify(this.messages))
        localStorage.setItem('error_messages_timestamp', Date.now().toString())
        
        this.initialized = true
      } catch (error) {
        console.warn('[ErrorHandler] Failed to load messages from server, using cached/fallback')
        
        // Пытаемся использовать закешированные
        const cached = localStorage.getItem('error_messages')
        if (cached) {
          try {
            this.messages = JSON.parse(cached)
            this.initialized = true
          } catch (e) {
            console.error('[ErrorHandler] Failed to parse cached messages')
          }
        }
        
        // Если нет кеша, используем минимальный fallback
        if (!this.initialized) {
          this.messages = this.getFallbackMessages()
          this.initialized = true
        }
      } finally {
        this.initPromise = null
      }
    })()

    return this.initPromise
  }

  /**
   * Fallback сообщения если не удалось загрузить с сервера
   */
  private getFallbackMessages(): ErrorMessages {
    return {
      http: [
        { code: 'http_400', pattern: null, message: 'Неверный запрос', is_default: true },
        { code: 'http_401', pattern: null, message: 'Сессия истекла. Войдите снова', is_default: true },
        { code: 'http_403', pattern: null, message: 'Нет прав доступа', is_default: true },
        { code: 'http_404', pattern: null, message: 'Ресурс не найден', is_default: true },
        { code: 'http_422', pattern: null, message: 'Ошибка валидации', is_default: true },
        { code: 'http_500', pattern: null, message: 'Ошибка сервера', is_default: true },
      ],
      network: [
        { code: 'network_error', pattern: null, message: 'Нет подключения к интернету', is_default: true },
        { code: 'network_timeout', pattern: 'timeout', message: 'Запрос слишком долгий', is_default: false },
      ]
    }
  }

  /**
   * Главный метод обработки ошибок
   * Возвращает объект с displayMessage и fieldErrors для использования в компонентах
   */
  handle(error: unknown, options?: {
    showDialog?: boolean
    logToConsole?: boolean
    redirectOnAuth?: boolean
  }): { displayMessage: string; fieldErrors: { [key: string]: string[] } } {
    const {
      showDialog = false, // По умолчанию НЕ показываем диалог (компонент сам решит)
      logToConsole = true,
      redirectOnAuth = true
    } = options || {}

    // Логирование для отладки
    if (logToConsole) {
      console.error('[ErrorHandler]', error)
    }

    // Извлекаем данные ошибки
    const errorData = this.extractErrorData(error)
    
    // Получаем человекопонятное сообщение
    const humanMessage = this.getHumanReadableMessage(errorData)

    // Специальная обработка для некоторых статусов
    if (errorData.status === 401 && redirectOnAuth) {
      this.handle401()
      return { displayMessage: humanMessage, fieldErrors: {} }
    }

    if (errorData.status === 403) {
      this.handle403(humanMessage, showDialog)
      return { displayMessage: humanMessage, fieldErrors: {} }
    }

    // Показываем диалог если запрошено
    if (showDialog) {
      this.showErrorDialog(humanMessage, errorData)
    }

    // Возвращаем данные для использования в компоненте
    return {
      displayMessage: humanMessage,
      fieldErrors: errorData.errors || {}
    }
  }

  /**
   * Извлекаем данные ошибки из разных форматов
   */
  private extractErrorData(error: unknown): ErrorData {
    if (error === null || typeof error !== 'object') {
      return {
        status: 500,
        message: typeof error === 'string' ? error : undefined,
        errors: {},
        details: undefined,
        isNetwork: false,
      }
    }

    const err = error as ErrorResponse
    const status = err.status || err.response?.status || 500
    const rawBody = err.body ?? err.response?.data

    let message: string | undefined
    let errors: Record<string, string[]> = {}

    if (typeof rawBody === 'string') {
      message = rawBody.trim() || undefined
    } else if (rawBody && typeof rawBody === 'object') {
      const data = rawBody as Record<string, unknown>
      const m = data.message
      message = typeof m === 'string' ? m : undefined

      const e = data.errors
      if (e && typeof e === 'object' && !Array.isArray(e)) {
        errors = e as Record<string, string[]>
      }
    }

    return {
      status,
      message: message ?? (typeof err.message === 'string' ? err.message : undefined),
      errors,
      details:
        typeof rawBody === 'object' && rawBody !== null
          ? (rawBody as { details?: unknown; error?: unknown }).details ??
            (rawBody as { details?: unknown; error?: unknown }).error
          : undefined,
      isNetwork: !err.response && !!(err as { request?: unknown }).request,
    }
  }

  /**
   * Получаем человекопонятное сообщение
   */
  private getHumanReadableMessage(errorData: ErrorData): string {
    const { status, message, errors, isNetwork } = errorData

    // Сетевая ошибка
    if (isNetwork) {
      return this.getNetworkErrorMessage(message)
    }

    // Ошибки валидации (422)
    if (status === 422 && errors && Object.keys(errors).length > 0) {
      const errorList = Object.values(errors).flat() as string[]
      return errorList.join('\n')
    }

    // Находим сообщение в словаре
    return this.getMessageFromDictionary(status, message)
  }

  /**
   * Получить сообщение для сетевой ошибки
   */
  private getNetworkErrorMessage(message?: string): string {
    const networkMessages = this.messages.network || []
    
    // Сначала ищем по паттерну
    if (message) {
      const patternMatch = networkMessages.find(msg => 
        msg.pattern && message.toLowerCase().includes(msg.pattern.toLowerCase())
      )
      if (patternMatch) return patternMatch.message
    }
    
    // Затем ищем default
    const defaultMessage = networkMessages.find(msg => 
      msg.code === 'network_error' && msg.is_default
    )
    
    return defaultMessage?.message || 'Нет подключения к интернету'
  }

  /**
   * Получить сообщение из словаря по статусу.
   * Если с сервера пришла причина ошибки (serverMessage) — показываем её пользователю,
   * иначе — сообщение из словаря или общий fallback.
   */
  private getMessageFromDictionary(status: number, serverMessage?: string): string {
    const code = `http_${status}`
    const httpMessages = this.messages.http || []
    const sanitized = this.sanitizeServerMessage(serverMessage)

    // Сначала ищем по паттерну в словаре
    if (serverMessage) {
      const patternMatch = httpMessages.find(msg =>
        msg.code === code &&
        msg.pattern &&
        serverMessage.includes(msg.pattern)
      )
      if (patternMatch) return patternMatch.message
    }

    // Приоритет: показывать причину ошибки с сервера (для всех статусов)
    if (sanitized) return sanitized

    // Затем default для этого кода из словаря
    const defaultMessage = httpMessages.find(msg =>
      msg.code === code && msg.is_default
    )
    if (defaultMessage) return defaultMessage.message

    return 'Произошла ошибка. Обратитесь в поддержку.'
  }

  /**
   * Очистка сообщения от сервера: убираем пути к файлам и технический мусор,
   * при необходимости переводим типичные SQL/PDO формулировки на понятный пользователю вид.
   */
  private sanitizeServerMessage(message?: string | null): string {
    if (!message || typeof message !== 'string') return ''
    let text = message.trim()
    if (!text) return ''

    // Убираем путь к файлу (например /var/www/vendor/...)
    text = text.replace(/\s*at\s+\/[\w\/.-]+\s*$/i, '').trim()
    // Убираем префикс [previous exception] [object] (PDOException...):
    text = text.replace(/^\[previous exception\]\s*\[object\]\s*\(\w+\([^)]*\):\s*/i, '')
    // Оставляем только текст после последней двоеточия, если это похоже на код (code: 23000)
    const codeMatch = text.match(/\d+\):\s*(.+)$/)
    if (codeMatch) text = codeMatch[1].trim()

    // Человекопонятные подсказки для типичных ошибок БД
    if (text.includes("Column 'markup' cannot be null") || text.includes("'markup' cannot be null")) {
      return 'Не заполнено поле «Наценка». Укажите число (например 0).'
    }
    if (text.includes("Column 'discount' cannot be null") || text.includes("'discount' cannot be null")) {
      return 'Не заполнено поле «Скидка». Укажите число (например 0).'
    }
    if (text.includes('cannot be null')) {
      const col = text.match(/Column '(\w+)' cannot be null/i)
      return col ? `Не заполнено обязательное поле «${col[1]}».` : text
    }

    return text
  }

  /**
   * Обработка 401 Unauthorized
   */
  private handle401(): void {
    console.log('[ErrorHandler] 401 Unauthorized - redirecting to login')
    localStorage.removeItem('access_token')
    window.location.href = '/login'
  }

  /**
   * Обработка 403 Forbidden
   */
  private handle403(message: string, showDialog: boolean): void {
    if (showDialog) {
      this.showErrorDialog(message, { status: 403, message, errors: {}, details: null, isNetwork: false })
    }
  }

  /**
   * Показываем диалог с ошибкой (публичный метод)
   * Используется когда компонент сам управляет отображением ошибки
   */
  showErrorSwal(message: string, title?: string): void {
    const primaryColor = (store.getters as any).company?.settings?.primary_color || '#1976D2'

    Swal.fire({
      icon: 'error',
      title: title || 'Произошла ошибка',
      text: message,
      confirmButtonText: 'ОК',
      confirmButtonColor: primaryColor
    })
  }

  /**
   * Показываем диалог с ошибкой (приватный метод)
   */
  private showErrorDialog(message: string, errorData: ErrorData): void {
    this.showErrorSwal(message, this.getErrorTitle(errorData.status))
  }

  /**
   * Получаем заголовок для ошибки
   */
  private getErrorTitle(status: number): string {
    const titles: Record<number, string> = {
      400: 'Неверный запрос',
      401: 'Не авторизован',
      403: 'Доступ запрещен',
      404: 'Не найдено',
      422: 'Ошибка валидации',
      500: 'Ошибка сервера'
    }
    return titles[status] || 'Произошла ошибка'
  }

  /**
   * Обновить сообщения (например, после изменения языка)
   */
  async refresh(): Promise<void> {
    this.initialized = false
    this.messages = {}
    localStorage.removeItem('error_messages')
    localStorage.removeItem('error_messages_timestamp')
    await this.init()
  }
}

// Экспортируем singleton
export default new ErrorHandler()

